import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { GROWTHBOOK_FEATURE_FLAGS } from '@web-app/constants/growthbook';
import { useIsMedicalDoctor } from '@web-app/hooks/useIsMedicalDoctor';
import { useRouter } from 'next/router';

/**
 * Hook to determine if the Messages/Chat route should be enabled.
 *
 * Priority (highest to lowest):
 * 1. URL query parameter ?chat=true|false (always overrides)
 * 2. Prescriber check — providers flagged `is_prescriber` in Airtable have chat disabled
 * 3. GrowthBook feature flag 'chat-enabled'
 */
export const useChatEnabled = (): boolean => {
  const router = useRouter();
  const growthBookEnabled = useFeatureIsOn(
    GROWTHBOOK_FEATURE_FLAGS.CHAT_ENABLED,
  );
  // Chat is temporarily disabled for prescribers. Hold the gate closed until
  // the prescriber check resolves to avoid a flash of enabled state on initial load.
  // On error, skip the prescriber check and fall back to the GrowthBook flag.
  const {
    isMedicalDoctor,
    isLoading: isMdLoading,
    isError: isMdError,
  } = useIsMedicalDoctor();

  const queryParam = router.query['chat'];
  if (queryParam === 'true') {
    return true;
  }
  if (queryParam === 'false') {
    return false;
  }

  if (!isMdError && (isMdLoading || isMedicalDoctor)) {
    return false;
  }

  return growthBookEnabled;
};
